/* Cache an array of images for animation purposes. */

/* Pass an array of image URLs, and the following options:

   - lookahead: try to be loading this many images at any one time, default 3
   - maxImages: keep at most this many images in memory, default all

   Then call .getImage(index) to retrieve that image. If it is loaded and
   in memory, it will be returned (as a HTMLImageelement). Otherwise, null
   is returned.

   At that time, new images will also start loading, if possible. It is expected
   that .getImage will be called many times per second, even if the index doesn't change.
*/

interface Options {
  lookahead?: number;
  maxImages?: number;
}

interface Image {
  url: string;
  image: HTMLImageElement | null;
}

export class ImageCache {
  width: number;
  height: number;
  lookahead: number;
  maxImages: number;
  images: Image[];

  constructor(urls: string[], width: number, height: number, options: Options = {}) {
    this.width = width;
    this.height = height;
    this.lookahead = options.lookahead ?? 3;
    this.maxImages = options.maxImages ?? urls.length;
    this.images = urls.map((url) => ({ url, image: null }));
  }

  remove() {
    this.images.forEach(({ image }) => {
      if (image !== null) {
        image.remove();
      }
    });
  }

  isSame(urls: string[], width: number, height: number, options: Options = {}): boolean {
    if (this.width !== width) return false;
    if (this.height !== height) return false;
    if (this.lookahead !== (options.lookahead ?? 3)) return false;
    if (this.maxImages !== (options.maxImages ?? urls.length)) return false;
    if (this.images.length !== urls.length) return false;
    return this.images.every((img, idx) => img.url === urls[idx]);
  }

  getImage(index: number) {
    if (index < 0 || index >= this.images.length) {
      return null;
    }

    const { image } = this.images[index];

    const result = image !== null && image.complete ? image : null;

    this._loadNewImages(index);

    return result;
  }

  _loadNewImages(startIndex: number) {
    let numLoading = 0;
    let numImages = 0;
    for (let i = 0; i < this.images.length; i++) {
      const idx = (i + startIndex) % this.images.length;
      const image = this.images[idx];

      // Starting from here, check the images; if an image
      // isn't loading yet that can be, add a new HTMLImageelement
      // to the array; if an image is loading that shouldn't be,
      // remove it; if there is an image when there shouldn't be,
      // remove it.
      if (image.image === null) {
        if (numLoading < this.lookahead) {
          image.image = document.createElement("img");
          // The below is to prevent CORS problems; no auth information
          // sent.
          image.image.crossOrigin = "anonymous";
          image.image.width = this.width;
          image.image.height = this.height;
          image.image.src = image.url;
          numImages++;
          numLoading++;
        }
      } else {
        if (numImages >= this.maxImages) {
          image.image.remove();
          image.image = null;
        } else if (!image.image.complete) {
          if (numLoading >= this.lookahead) {
            image.image.remove();
            image.image = null;
          } else {
            numImages++;
            numLoading++;
          }
        } else {
          numImages++;
        }
      }
    }
  }
}
